import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Alert, Button, DatePicker, Modal, Select, Spin } from 'antd';
import PropTypes from 'prop-types';
import { fetchMedications } from '../../../medications/api/medicationsApi';
import { renewTherapy } from '../../api/therapiesApi';
import fixedBottomDatePickerProps from '../../../../shared/utils/datePickerPlacement';
import {
  getTherapyPatientName,
  getTherapyTypeLabel,
} from '../../utils/therapyPresentation';

const initialForm = {
  centralDate: '',
  durationMonths: '',
  medications: [],
};

const durationOptions = ['1', '2', '3', '4', '5', '6'];

// Backend uz obnovu vraca reservationAdjustment kada je rezervacija skracena.
const getReservationAdjustment = (data) =>
  data?.reservationAdjustment || data?.data?.reservationAdjustment || null;

const RenewTherapyModal = ({
  isOpen,
  onClose,
  therapy,
  onRenewed,
  onLoadingChange,
}) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [medicationOptions, setMedicationOptions] = useState([]);
  const [medicationsLoading, setMedicationsLoading] = useState(false);
  const [medicationSearch, setMedicationSearch] = useState('');
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const loadMedications = async () => {
      setMedicationsLoading(true);

      const { data, ok, message } = await fetchMedications({
        page: 1,
        limit: 10000,
      });

      if (cancelled) return;

      if (!ok) {
        setMedicationOptions([]);
        setError(message || 'Doslo je do greske pri dobavljanju lekova.');
        setMedicationsLoading(false);
        return;
      }

      const options = Array.isArray(data?.medications)
        ? data.medications.map((medication) => ({
            value: medication.name,
            label: medication.name,
          }))
        : [];

      setMedicationOptions(options);
      setMedicationsLoading(false);
    };

    setForm({
      centralDate: '',
      durationMonths: therapy?.isAcute ? '1' : '',
      medications: Array.isArray(therapy?.medications)
        ? therapy.medications
        : [],
    });
    setMedicationSearch('');
    setError('');
    setSuccessMessage('');
    setDone(false);
    setLoading(false);
    setMedicationOptions([]);
    setConfirmVisible(false);
    setPendingPayload(null);

    loadMedications();

    return () => {
      cancelled = true;
    };
  }, [isOpen, therapy]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectMedication = (value) => {
    setForm((prev) => ({
      ...prev,
      medications: prev.medications.includes(value)
        ? prev.medications
        : [...prev.medications, value],
    }));

    setMedicationSearch('');
  };

  const handleRemoveMedication = (index) => {
    setForm((prev) => ({
      ...prev,
      medications: prev.medications.filter((_, i) => i !== index),
    }));
  };

  const submitRenew = useCallback(
    async (payload) => {
      setLoading(true);
      onLoadingChange?.(true);
      setError('');
      setSuccessMessage('');

      try {
        const { data, ok, message } = await renewTherapy(therapy._id, payload);
        if (!ok) {
          setError(message);
          return;
        }

        // Ako je backend skratio rezervaciju, potvrdi to korisniku.
        const adjustment = getReservationAdjustment(data);
        const renewMessage = message || 'Terapija obnovljena.';
        const isShortened =
          adjustment &&
          Number(adjustment.previousCount) !== Number(adjustment.newCount);
        const successText = isShortened
          ? `${renewMessage} Rezervacija skracena sa ${adjustment.previousCount} na ${adjustment.newCount} mes.`
          : renewMessage;

        setSuccessMessage(successText);
        setDone(true);
        await onRenewed?.(data);
        setTimeout(
          () => {
            setDone(false);
            onClose?.();
          },
          isShortened ? 1600 : 800
        );
      } finally {
        setLoading(false);
        onLoadingChange?.(false);
      }
    },
    [onClose, onLoadingChange, onRenewed, therapy?._id]
  );

  const handleOk = useCallback(async () => {
    if (!therapy?._id) {
      setError('Terapija nije izabrana.');
      return;
    }
    const centralDate = form.centralDate?.trim();
    if (!centralDate) {
      setError('Popunite centralni datum.');
      return;
    }
    if (!therapy?.isAcute && !form.durationMonths) {
      setError('Izaberite trajanje.');
      return;
    }

    const effectiveDuration = Number(
      therapy?.isAcute ? '1' : form.durationMonths
    );
    const payload = {
      centralDate,
      durationMonths: effectiveDuration,
      medications: form.medications || [],
    };

    // Edge: obnova kraca od postojece rezervacije -> potvrda pre slanja.
    const reservationCount = Number(therapy?.reservation?.count) || 0;
    if (reservationCount > effectiveDuration) {
      setPendingPayload(payload);
      setConfirmVisible(true);
      return;
    }

    await submitRenew(payload);
  }, [
    form,
    submitRenew,
    therapy?._id,
    therapy?.isAcute,
    therapy?.reservation?.count,
  ]);

  const handleConfirmRenew = useCallback(async () => {
    const payload = pendingPayload;
    setConfirmVisible(false);
    setPendingPayload(null);
    if (payload) {
      await submitRenew(payload);
    }
  }, [pendingPayload, submitRenew]);

  const handleConfirmCancel = useCallback(() => {
    setConfirmVisible(false);
    setPendingPayload(null);
  }, []);

  const handleCancel = () => {
    setForm(initialForm);
    setMedicationSearch('');
    setError('');
    setSuccessMessage('');
    setDone(false);
    setConfirmVisible(false);
    setPendingPayload(null);
    onLoadingChange?.(false);
    onClose?.();
  };

  const availableMedicationOptions = medicationOptions.filter(
    (option) => !form.medications.includes(option.value)
  );

  return (
    <Modal
      className="create_user_modal"
      title="Obnovi terapiju"
      open={isOpen}
      onCancel={handleCancel}
      footer={[
        <Button
          key="renew"
          type="primary"
          onClick={handleOk}
          disabled={loading || done}
          style={{ width: '170px' }}
        >
          {loading ? (
            <img
              src="/assets/icons/add.svg"
              alt="Učitavanje"
              className="animate-spin"
            />
          ) : done ? (
            <img
              src="/assets/icons/check.svg"
              alt="Done"
              className="check-icon"
            />
          ) : (
            'Obnovi terapiju'
          )}
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <div className="create_user_modal_form" autoComplete="off">
        <div style={{ marginBottom: 12 }}>
          <strong>Pacijent:</strong> {getTherapyPatientName(therapy)}
        </div>
        <div style={{ marginBottom: 12 }}>
          <strong>Tip:</strong> {getTherapyTypeLabel(therapy)}
        </div>
        {therapy?.isAcute && (
          <Alert
            title="Akutna terapija se obnavlja kao akutna i traje 1 mesec."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="centralDate"
            style={{ display: 'block', marginBottom: 6 }}
          >
            Novi centralni datum
          </label>
          <DatePicker
            {...fixedBottomDatePickerProps}
            id="centralDate"
            placeholder="dd/mm/yyyy"
            format="DD/MM/YYYY"
            style={{ width: '100%', height: 38 }}
            value={form.centralDate ? dayjs(form.centralDate) : null}
            onChange={(date) =>
              setForm((prev) => ({
                ...prev,
                centralDate: date ? date.format('YYYY-MM-DD') : '',
              }))
            }
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="durationMonths"
            style={{ display: 'block', marginBottom: 6 }}
          >
            Trajanje (meseci)
          </label>
          <select
            id="durationMonths"
            name="durationMonths"
            value={form.durationMonths}
            onChange={handleChange}
            disabled={therapy?.isAcute}
            style={{
              width: '100%',
              height: 38,
              borderRadius: 8,
              border: '1px solid #d9d9d9',
              padding: '0 10px',
            }}
          >
            <option value="" disabled>
              Izaberite trajanje
            </option>
            {durationOptions.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor="renew-therapy-medication-select"
            style={{ display: 'block', marginBottom: 6 }}
          >
            Lekovi
          </label>
          <Select
            id="renew-therapy-medication-select"
            showSearch
            value={null}
            searchValue={medicationSearch}
            placeholder={
              medicationsLoading
                ? 'Ucitavanje lekova...'
                : 'Izaberite lek iz sistema'
            }
            style={{ width: '100%' }}
            loading={medicationsLoading}
            disabled={
              medicationsLoading || availableMedicationOptions.length === 0
            }
            options={availableMedicationOptions}
            filterOption={(input, option) =>
              String(option?.label || '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            notFoundContent={
              medicationsLoading
                ? 'Ucitavanje lekova...'
                : 'Nema dostupnih lekova'
            }
            onSearch={setMedicationSearch}
            onSelect={handleSelectMedication}
          />
          <div className="therapy-med-list">
            {form.medications.map((med, index) => (
              <div key={`${med}-${index}`} className="therapy-med-chip">
                <span>{med}</span>
                <button
                  type="button"
                  className="therapy-med-remove"
                  onClick={() => handleRemoveMedication(index)}
                >
                  x
                </button>
              </div>
            ))}
          </div>
        </div>
        {successMessage && (
          <Alert
            title={successMessage}
            type="success"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
        {error && (
          <Alert
            title={error}
            type="error"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
        </div>
      </Spin>
      <Modal
        className="therapy_confirm_modal"
        title="Rezervisana terapija"
        open={confirmVisible}
        onOk={handleConfirmRenew}
        onCancel={handleConfirmCancel}
        okText="Nastavi"
        cancelText="Odustani"
        centered
        destroyOnHidden
      >
        {`Za ovu terapiju je predodredjena rezervacija od ${
          therapy?.reservation?.count ?? '-'
        } meseca. Preostali rezervisani meseci bice odbaceni. Zelite li da nastavite?`}
      </Modal>
    </Modal>
  );
};

RenewTherapyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  therapy: PropTypes.object,
  onRenewed: PropTypes.func,
  onLoadingChange: PropTypes.func,
};

export default RenewTherapyModal;
